import { useEffect, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { SoftButton, SoftFooter, SoftRowSkeleton, SoftTag, cls, useIsDark } from "../../hatim/soft/SoftKit";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../components/ui/Toast";
import {
  useGetManagedHatimTemplateQuery,
  useRemoveHatimTemplateMutation,
} from "../../../api/hatimManagementApi";
import { isRangeType } from "../../../lib/hatimType";
import { remainingToAssign } from "../../../lib/hatimAssignment";
import RepeatDurationInline from "./RepeatDurationInline";
import HatimProgressList from "./HatimProgressList";
import HatimTemplateManagersList from "./HatimTemplateManagersList";
import AddHatimManagerModal from "./AddHatimManagerModal";
import HatimTemplateMembersList from "./HatimTemplateMembersList";
import HatimUserAssignmentModal from "./HatimUserAssignmentModal";
import HatimParticipationList from "./HatimParticipationList";
import AddParticipationCodeModal from "./AddParticipationCodeModal";
import DeleteHatimTemplateConfirm from "./DeleteHatimTemplateConfirm";

const TAB_PROGRESS = 0;
const TAB_MEMBERS = 1;
const TAB_MANAGERS = 2;
const TAB_CODES = 3;
const TAB_SETTINGS = 4;

const TR_MONTHS = [
  "Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
  "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık",
];
function formatTrDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return `${d.getDate()} ${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Sağ panel: seçili hatimin tüm yönetimi: ilerleme, üyeler, yöneticiler,
 * davet kodları ve silme. Liste öğesi `template` hemen gösterilir, tam
 * detay (populate edilmiş üyeler, kodlar) arka planda gelir.
 */
export default function HatimDetailPanel({ template, onDeleted }) {
  const { t } = useScriptMode();
  const dark = useIsDark();
  const { user } = useAuth();
  const toast = useToast();
  const currentUserId = user?._id;

  const templateId = template ? String(template._id) : null;
  const [tab, setTab] = useState(TAB_PROGRESS);
  const [addManagerOpen, setAddManagerOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [addCodeOpen, setAddCodeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    setTab(TAB_PROGRESS);
  }, [templateId]);

  const { data, isFetching: detailLoading } = useGetManagedHatimTemplateQuery(
    { hatimTemplateId: templateId },
    { skip: !templateId }
  );
  const detail = data?.data?.template || data?.data || null;
  const [removeTpl, { isLoading: deleteLoading }] = useRemoveHatimTemplateMutation();

  if (!template) {
    return (
      <div className={`${cls.panel} flex flex-col items-center justify-center text-center min-h-[320px] lg:h-full`}>
        <MenuBookOutlinedIcon sx={{ fontSize: 40 }} className={cls.faint} aria-hidden />
        <p className={`text-base mt-3 ${cls.muted}`}>{t("hatim_mgmt_pick_hint")}</p>
      </div>
    );
  }

  const tpl = detail && String(detail._id) === templateId ? detail : template;
  const isRange = isRangeType(tpl.type);
  const detailUsers = detail?.users || [];
  const remaining = remainingToAssign(detailUsers, tpl.type, tpl.totalAmount);
  const nothingLeft = Boolean(detail) && Number(tpl.totalAmount) > 0 && remaining === 0;

  const handleDelete = async () => {
    try {
      await removeTpl({ hatimTemplateId: templateId }).unwrap();
      toast.success(t("hatim_mgmt_delete_success"));
      setDeleteOpen(false);
      onDeleted && onDeleted(templateId);
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("hatim_mgmt_action_error");
      toast.error(msg);
    }
  };

  const tabs = [
    t("hatim_progress_title"),
    t("hatim_mgmt_section_users"),
    t("hatim_mgmt_section_managers"),
    t("hatim_mgmt_section_participation"),
    t("hatim_mgmt_tab_settings"),
  ];

  // Her sekmenin işlemi altta, çizgiyle ayrılmış sabit bölgede (mobildeki gibi).
  let footer = null;
  if (tab === TAB_MEMBERS) {
    footer = (
      <SoftFooter className="items-center">
        {nothingLeft ? (
          <span className={`text-xs flex-1 ${cls.muted}`}>{t("hatim_mgmt_members_nothing_left")}</span>
        ) : null}
        <SoftButton
          tone="primary"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setAddUserOpen(true)}
          disabled={nothingLeft || (detailLoading && !detail)}
          sx={nothingLeft ? undefined : { flex: 1 }}
        >
          {t("hatim_mgmt_add_user_cta")}
        </SoftButton>
      </SoftFooter>
    );
  } else if (tab === TAB_MANAGERS) {
    footer = (
      <SoftFooter>
        <SoftButton tone="primary" fullWidth startIcon={<AddIcon fontSize="small" />} onClick={() => setAddManagerOpen(true)}>
          {t("hatim_mgmt_add_manager_cta")}
        </SoftButton>
      </SoftFooter>
    );
  } else if (tab === TAB_CODES) {
    footer = (
      <SoftFooter>
        <SoftButton tone="primary" fullWidth startIcon={<AddIcon fontSize="small" />} onClick={() => setAddCodeOpen(true)}>
          {t("hatim_mgmt_add_participation_cta")}
        </SoftButton>
      </SoftFooter>
    );
  } else if (tab === TAB_SETTINGS) {
    footer = (
      <SoftFooter>
        <SoftButton tone="danger" fullWidth startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={() => setDeleteOpen(true)}>
          {t("hatim_mgmt_delete_cta")}
        </SoftButton>
      </SoftFooter>
    );
  }

  const rowSkeleton = <SoftRowSkeleton rows={3} />;

  return (
    <div
      className={`${cls.panel} overflow-hidden flex flex-col min-h-[320px] max-h-[calc(100vh-3rem)] lg:h-full lg:max-h-full lg:min-h-0`}
    >
      <div className={`shrink-0 px-4 py-3 ${cls.summary}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex items-center gap-3">
            <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${cls.avatar}`}>
              <MenuBookOutlinedIcon sx={{ fontSize: 20 }} aria-hidden />
            </span>
            <div className="min-w-0">
              <div className={`text-xl font-SemiBold leading-tight truncate ${cls.heading}`}>{tpl.name}</div>
              <div className={`text-xs leading-tight mt-0.5 ${cls.muted}`}>
                {t("hatim_mgmt_card_total")}: {tpl.totalAmount} · {formatTrDate(tpl.startDate)} - {formatTrDate(tpl.endDate)}
                {tpl.repeatDuration ? ` · ${t("hatim_mgmt_card_repeat")}: ${tpl.repeatDuration}` : ""}
              </div>
            </div>
          </div>
          <SoftTag>{tpl.type}</SoftTag>
        </div>
      </div>

      <div className={`shrink-0 px-2 border-b border-terakkiBorder dark:border-white/[0.14]`}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": { backgroundColor: dark ? "#8FC9CA" : "#006A71" },
            "& .MuiTab-root.Mui-selected": { color: dark ? "#FFFFFF" : "#006A71" },
            "& .MuiTab-root": { color: dark ? "rgba(255,255,255,0.7)" : undefined },
          }}
        >
          {tabs.map((label) => (
            <Tab key={label} label={label} sx={{ minHeight: 48, textTransform: "none", fontSize: 15, fontWeight: 500 }} />
          ))}
        </Tabs>
      </div>

      {/* İlerleme sekmesinde üst kısım sabit, yalnızca üye listesi kayar. */}
      <div
        className={`flex-1 min-h-0 ${
          tab === TAB_PROGRESS ? "p-4 lg:overflow-hidden lg:flex lg:flex-col overflow-y-auto" : "overflow-y-auto"
        }`}
      >
        {tab === TAB_PROGRESS && <HatimProgressList hatimTemplateId={templateId} />}

        {tab === TAB_MEMBERS &&
          (detailLoading && !detail ? (
            rowSkeleton
          ) : (
            <HatimTemplateMembersList
              hatimTemplateId={templateId}
              users={detailUsers}
              type={tpl.type}
              isRange={isRange}
              totalAmount={tpl.totalAmount}
              repeatDuration={tpl.repeatDuration}
              onEdit={(u) => setEditUser(u)}
            />
          ))}

        {tab === TAB_MANAGERS &&
          (detailLoading && !detail ? (
            rowSkeleton
          ) : (
            <HatimTemplateManagersList
              hatimTemplateId={templateId}
              managers={detail?.managers || []}
              currentUserId={currentUserId}
            />
          ))}

        {tab === TAB_CODES && (
          <HatimParticipationList
            hatimTemplateId={templateId}
            codes={detail?.participationTemplates || []}
          />
        )}

        {tab === TAB_SETTINGS && (
          <div>
            <section className={`px-4 py-4 ${cls.row}`}>
              <RepeatDurationInline
                hatimTemplateId={templateId}
                currentValue={tpl.repeatDuration}
                minValue={tpl.currentPeriod || 1}
              />
            </section>
            <section className="px-4 py-4">
              <h3 className={`text-base font-Medium ${cls.danger}`}>{t("hatim_mgmt_danger_zone")}</h3>
              <p className={`text-sm mt-1 ${cls.muted}`}>{t("hatim_mgmt_delete_modal_body")}</p>
            </section>
          </div>
        )}
      </div>

      {footer}

      <AddHatimManagerModal
        open={addManagerOpen}
        hatimTemplateId={templateId}
        onClose={() => setAddManagerOpen(false)}
        onAdded={() => toast.success(t("hatim_mgmt_manager_added"))}
      />
      <HatimUserAssignmentModal
        open={addUserOpen}
        mode="add"
        hatimTemplateId={templateId}
        type={tpl.type}
        totalAmount={tpl.totalAmount}
        users={detailUsers}
        onClose={() => setAddUserOpen(false)}
        onSaved={() => toast.success(t("hatim_mgmt_user_added"))}
      />
      <HatimUserAssignmentModal
        open={!!editUser}
        mode="edit"
        hatimTemplateId={templateId}
        type={tpl.type}
        totalAmount={tpl.totalAmount}
        users={detailUsers}
        member={editUser}
        onClose={() => setEditUser(null)}
        onSaved={() => toast.success(t("hatim_mgmt_user_assignment_updated"))}
      />
      <AddParticipationCodeModal
        open={addCodeOpen}
        hatimTemplateId={templateId}
        onClose={() => setAddCodeOpen(false)}
        onCreated={() => toast.success(t("hatim_mgmt_participation_added"))}
      />
      <DeleteHatimTemplateConfirm
        open={deleteOpen}
        name={tpl.name}
        loading={deleteLoading}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
